import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { useAuth, API } from "@/App";
import axios from "axios";
import { QrCode, Download, Copy, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const QRCodePage = () => {
  const [url, setUrl] = useState("");
  const [fgColor, setFgColor] = useState("#7c3aed");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [size, setSize] = useState([300]);
  const [qrCode, setQrCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateQRCode = async () => {
    if (!url) {
      toast.error("Masukkan URL terlebih dahulu");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/qrcode`, {
        url,
        fg_color: fgColor,
        bg_color: bgColor,
        size: size[0]
      });
      setQrCode(response.data.qr_code);
      toast.success("QR Code berhasil dibuat!");
    } catch (error) {
      toast.error("Gagal membuat QR Code");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCode) return;

    const link = document.createElement("a");
    link.href = qrCode;
    link.download = `qrcode-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR Code berhasil didownload!");
  };

  const copyToClipboard = async () => {
    if (!qrCode) return;

    try {
      const response = await fetch(qrCode);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob })
      ]);
      setCopied(true);
      toast.success("QR Code disalin!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback: copy data URL
      try {
        await navigator.clipboard.writeText(qrCode);
        setCopied(true);
        toast.success("QR Code URL disalin!");
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        toast.error("Gagal menyalin QR Code");
      }
    }
  };

  const presetColors = [
    { fg: "#7c3aed", bg: "#ffffff", name: "Purple" },
    { fg: "#000000", bg: "#ffffff", name: "Classic" },
    { fg: "#3b82f6", bg: "#ffffff", name: "Blue" },
    { fg: "#10b981", bg: "#ffffff", name: "Green" },
    { fg: "#ec4899", bg: "#ffffff", name: "Pink" },
    { fg: "#ffffff", bg: "#1e1b4b", name: "Dark" }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qrcode-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
            QR Code Generator
          </h1>
          <p className="text-muted-foreground mt-1">
            Buat QR code dengan warna kustom
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Settings */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Settings</CardTitle>
              <CardDescription>Kustomisasi QR code Anda</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* URL Input */}
              <div className="space-y-2">
                <Label>URL</Label>
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  data-testid="qr-url-input"
                />
              </div>

              {/* Color Presets */}
              <div className="space-y-2">
                <Label>Color Presets</Label>
                <div className="grid grid-cols-3 gap-2">
                  {presetColors.map((preset, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className={`h-auto py-3 flex flex-col items-center gap-2 ${
                        fgColor === preset.fg && bgColor === preset.bg ? "border-primary" : ""
                      }`}
                      onClick={() => {
                        setFgColor(preset.fg);
                        setBgColor(preset.bg);
                      }}
                      data-testid={`preset-${index}`}
                    >
                      <div 
                        className="w-8 h-8 rounded border"
                        style={{ 
                          backgroundColor: preset.bg,
                          borderColor: preset.fg
                        }}
                      >
                        <div 
                          className="w-full h-full rounded grid grid-cols-3 grid-rows-3 gap-0.5 p-1"
                        >
                          {[0,1,2,3,4,5,6,7,8].map(i => (
                            <div 
                              key={i}
                              className="rounded-sm"
                              style={{ 
                                backgroundColor: [0,2,4,6,8].includes(i) ? preset.fg : "transparent"
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      <span className="text-xs">{preset.name}</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Foreground Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="w-10 h-10 rounded border cursor-pointer"
                      data-testid="fg-color-input"
                    />
                    <Input
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Background Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="w-10 h-10 rounded border cursor-pointer"
                      data-testid="bg-color-input"
                    />
                    <Input
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Size */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Size</Label>
                  <span className="text-sm text-muted-foreground">{size[0]}px</span>
                </div>
                <Slider
                  value={size}
                  onValueChange={setSize}
                  min={100}
                  max={500}
                  step={50}
                  data-testid="size-slider"
                />
              </div>

              {/* Generate Button */}
              <Button 
                className="w-full h-12" 
                onClick={generateQRCode}
                disabled={isLoading}
                data-testid="generate-btn"
              >
                {isLoading ? (
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <QrCode className="w-5 h-5 mr-2" />
                )}
                {isLoading ? "Generating..." : "Generate QR Code"}
              </Button>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Preview</CardTitle>
              <CardDescription>QR code hasil generate</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center">
                <div 
                  className="qr-container rounded-2xl p-8 mb-6"
                  style={{ backgroundColor: bgColor }}
                >
                  {qrCode ? (
                    <img 
                      src={qrCode} 
                      alt="QR Code"
                      style={{ width: size[0], height: size[0] }}
                      data-testid="qr-image"
                    />
                  ) : (
                    <div 
                      className="flex items-center justify-center border-2 border-dashed border-muted rounded-xl"
                      style={{ width: size[0], height: size[0] }}
                    >
                      <div className="text-center text-muted-foreground">
                        <QrCode className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">QR Code akan muncul di sini</p>
                      </div>
                    </div>
                  )}
                </div>

                {qrCode && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={downloadQRCode}
                      data-testid="download-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      onClick={copyToClipboard}
                      data-testid="copy-qr-btn"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default QRCodePage;
